import React, { forwardRef } from 'react'
import { cn } from '../../utils/cn'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'filter' | 'subtle' | 'orange' | 'outline' | 'forum' | 'condo'
  size?: 'sm' | 'md' | 'lg'
  asChild?: boolean
  loading?: boolean
  icon?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  className = 'sm:text-base text-sm',
  asChild = false,
  loading = false,
  icon,
  children,
  disabled,
  ...props
}, ref) => {
  const baseStyles = cn(
    'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'active:scale-[0.98]'
  )
  
  const variants = {
    primary: cn(
      'bg-condo-dark text-light-3 lg:hover:bg-condo-med active:bg-condo-light',
      'focus:ring-condo-dark'
    ),
    secondary: cn(
      'bg-dark-1 text-condo-dark border border-dark-2 hover:bg-dark-2 hover:border-condo-dark active:hover',
      'focus:ring-condo-dark'
    ),
    filter: cn(
      'bg-dark-2 text-condo-dark border border-condo-dark hover:bg-condo-dark hover:border-condo-dark hover:text-condo-light active:hover',
      'focus:ring-condo-dark'
    ),
    subtle: cn(
      'border border-gray-300 text-gray-700 hover:bg-dark-1 active:bg-light-2',
      'focus:ring-gray-400'
    ),
    orange: cn(
      'bg-condo-orange text-gray-800 border border-orange-8 hover:bg-orange-8 active:bg-orange-10',
      'focus:ring-orange-500'
    ),
    outline: cn(
      'border border-gray-300 text-gray-700 lg:hover:bg-gray-50 active:bg-gray-100',
      'focus:ring-gray-400'
    ),
    forum: cn(
      'border border-condo-light bg-light-2 hover:bg-condo-light text-condo-dark',
      'focus:ring-condo-light'
    ),
    condo: cn(
      'bg-condo-dark text-light-3 hover:bg-dark-1 hover:text-condo-dark border hover:border-condo-dark',
      'focus:ring-condo-dark'
    )
  }

  const sizes = {
    sm: 'px-3 py-1.5 text-sm whitespace-nowrap',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  }

  const Comp = asChild ? 'span' : 'button'

  return (
    <Comp
      ref={ref}
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={loading || disabled}
      aria-disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <div 
          className="flex items-center gap-2"
          role="status"
          aria-label="Laster..."
        >
          <svg 
            className="animate-spin h-4 w-4" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
              fill="none"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Laster...</span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {icon && <span aria-hidden="true">{icon}</span>}
          {children}
        </div>
      )}
    </Comp>
  )
})

Button.displayName = 'Button'

export { Button }